import React, { useRef, useState, useEffect } from "react";

const OneCareWorks = ({ section }) => {
  const scrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll position
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollLeft = el.scrollLeft;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < maxScrollLeft - 5);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll(); // initial

    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  // Scroll functions
  const scrollNext = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  const scrollPrev = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };
  
  if (!section) return null;
  const { items = [] } = section || {};

  return (
    <div className="relative">
      {/* LEFT BUTTON */}
      <div className="absolute left-0 top-1/4 -translate-y-1/2 z-10 p-2 rounded-full shadow">
        <button
          onClick={canScrollLeft ? scrollPrev : undefined}
          disabled={!canScrollLeft}
          className={`p-2 rounded-full shadow-xl transition active:scale-90
            ${
              canScrollLeft
                ? "bg-[#1E2A5A] text-white hover:bg-[#2a3a7a]"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      {/* RIGHT BUTTON */}
      <div className="absolute right-0 top-1/4 -translate-y-1/2 z-10 p-2 rounded-full shadow">
        <button
          onClick={canScrollRight ? scrollNext : undefined}
          disabled={!canScrollRight}
          className={`p-2 rounded-full shadow-xl transition active:scale-90
            ${
              canScrollRight
                ? "bg-[#1E2A5A] text-white hover:bg-[#2a3a7a]"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* SCROLL CONTAINER */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto gap-6 no-scrollbar snap-x snap-mandatory pb-4"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {section.map((card, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-18px)] xl:w-[calc(25%-18px)] snap-start flex flex-col h-full bg-[#F8F9FF] rounded-xl overflow-hidden shadow-sm border border-slate-100"
          >
            {/* Image */}
            <div className="h-48 overflow-hidden">
              <img
                src={card.image}
                alt={card.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Middle */}
            <div className="p-6 flex-grow md:h-40">
              <h3 className="text-[#5569A0] font-bold tracking-widest uppercase mb-3">
                {card.title}
              </h3>
              <p className="  font-medium leading-snug">
                {card.subtitle}
              </p>
            </div>

            {/* Bottom */}
            <div className="bg-[#162975] p-6 m-2 rounded-xl text-white">
              <ul
                className={`grid gap-y-3 ${
                  card.twoCol ? "grid-cols-2 gap-x-2" : "grid-cols-1"
                }`}
              >
                {card.points.map((point, pIdx) => (
                  <li
                    key={pIdx}
                    className="font-light flex items-start opacity-90"
                  >
                    <span className="mr-2 opacity-60">-</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OneCareWorks;
